import { useMemo } from 'react';
import { useStore } from '../store/useStore';
import { computeStats } from '../lib/textStats';
import { useT } from '../i18n/useT';
import { formatNumber } from '../lib/format';
import { readWordGoal } from '../lib/wordGoal';

export function StatusBar() {
  const t = useT();
  const content = useStore((s) => s.activeDoc.content);
  const vimMode = useStore((s) => s.vimMode);
  const stats = useMemo(() => computeStats(content), [content]);
  const goal = useMemo(() => readWordGoal(content), [content]);

  return (
    <footer className="mb-statusbar">
      {goal ? (
        <GoalProgress
          words={stats.words}
          goal={goal}
          label={t('status.wordsOfGoal', {
            n: formatNumber(stats.words),
            goal: formatNumber(goal),
          })}
        />
      ) : (
        <span>{t('status.words', { n: formatNumber(stats.words) })}</span>
      )}
      <span>{t('status.chars', { n: formatNumber(stats.chars) })}</span>
      <span>{t('status.reading', { n: stats.readingMinutes })}</span>
      <span className="spacer" />
      {vimMode && <span>VIM</span>}
      <span>markbird</span>
    </footer>
  );
}

/**
 * Words against a goal, with a small ring that fills as you write.
 *
 * The text carries the meaning — "412 / 1,000 words" — so the ring is decorative
 * and hidden from assistive technology rather than announced as a second,
 * wordless version of the same number. Past the goal the ring stays full and
 * takes the accent colour, instead of overflowing.
 */
function GoalProgress({ words, goal, label }: { words: number; goal: number; label: string }) {
  const ratio = Math.min(1, words / goal);
  const reached = words >= goal;
  const radius = 5;
  const circumference = 2 * Math.PI * radius;
  return (
    <span className={`mb-goal${reached ? ' mb-goal--reached' : ''}`}>
      <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
        <circle className="mb-goal-track" cx="7" cy="7" r={radius} />
        <circle
          className="mb-goal-fill"
          cx="7"
          cy="7"
          r={radius}
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - ratio)}
        />
      </svg>
      {label}
    </span>
  );
}
